/**
 * God-mode frame profiler — answers "where is the performance being hurt?" live.
 * Spec: docs/superpowers/specs/2026-07-19-god-perf-overlay-design.md
 *
 * PerfCore is the pure aggregation layer (no DOM, no storage) so it runs under
 * node for tests (client/test/_perfcore.mts). PerfMonitor — the exported `perf`
 * singleton — adds god gating, localStorage persistence and the corner overlay.
 *
 * Timing model: main.ts brackets phases of frame() with begin/end; a bucket may
 * open several times per frame (the loop interleaves systems) — laps ACCUMULATE.
 * frameEnd() stamps the end of measured CPU work; frameStart(now) finalizes the
 * PREVIOUS frame into the ring:
 *   total     = now − prevStart                  (rAF-to-rAF)
 *   other     = cpuSpan − Σ buckets              (unmeasured in-frame CPU)
 *   gpu/vsync = total − cpuSpan                  (the gap OUTSIDE the callback:
 *                                                 rAF scheduling latency, vsync wait,
 *                                                 GPU backpressure, compositor)
 * Small CPU buckets + a large gpu/vsync row therefore means "not your JS".
 */

export const PERF_BUCKETS = ["sim", "world", "actors", "fx", "ui", "audio", "render"] as const;
export type PerfBucket = (typeof PERF_BUCKETS)[number];

/** Drill-down sub-zones, nested inside a parent bucket's laps. They measure the
 *  same wall-clock twice ON PURPOSE (parent + child), so `other` and the
 *  cpu-sum derivations must only ever total the top-level buckets. */
export const PERF_SUBS = {
  world: ["settlements"],
  actors: ["npcs"],
  fx: ["streams", "ocean", "weather", "bolts", "trails"],
  render: ["wx-bake", "reflection", "draw"],
} as const satisfies Partial<Record<PerfBucket, readonly string[]>>;
export type PerfSub = (typeof PERF_SUBS)[keyof typeof PERF_SUBS][number];
export type PerfZone = PerfBucket | PerfSub;

/** All measured zones — top-level buckets FIRST (their indices feed `hot` and
 *  the cpu sum), subs after. Stats arrays follow this order. */
export const ALL_ZONES = [
  ...PERF_BUCKETS,
  ...Object.values(PERF_SUBS).flat(),
] as readonly PerfZone[];

const N = PERF_BUCKETS.length;
const NA = ALL_ZONES.length;
const IDX = Object.fromEntries(ALL_ZONES.map((b, i) => [b, i])) as Record<PerfZone, number>;

/** Ring capacity ~4s at 60fps — comfortably spans the worst window. */
const RING = 256;
const AVG_WINDOW_MS = 500;
const WORST_WINDOW_MS = 3000;

export interface PerfStats {
  fps: number;
  frameAvg: number;
  frameWorst: number;
  /** Per-zone ms, ALL_ZONES order (top-level buckets first, then subs). */
  avg: number[];
  worst: number[];
  otherAvg: number;
  otherWorst: number;
  gpuAvg: number;
  gpuWorst: number;
  /** PERF_BUCKETS index of the biggest bucket in the window's single worst
   *  frame; -1 when the window holds no frames. */
  hot: number;
}

export class PerfCore {
  /** One boolean gate — when false every call is a no-op (the always-compiled
   *  call sites in main.ts must cost nothing measurable). */
  enabled = true;
  protected clock: () => number;
  private startT = 0;
  /**
   * Wall clock at the top of the frame CALLBACK — deliberately not `startT`.
   *
   * `startT` is the rAF timestamp, which is when the frame was SCHEDULED, and
   * the callback can run some milliseconds after it. Measuring the CPU span
   * against that timestamp folds the scheduling gap into `cpuSpan`, and the two
   * derived rows then lie in opposite directions: `other` (cpuSpan − Σ buckets)
   * inflates by the gap, and `gpu/vsync` (total − cpuSpan) clamps to zero. That
   * is exactly what the overlay was showing — `other` above 10 ms and gpu/vsync
   * pinned at 0.00 while the GPU was plainly saturated. The frame TOTAL still
   * wants the rAF timestamp, so the two starts have to be kept apart.
   */
  private cpuStartT = 0;
  private cpuSpan = 0;
  private hasFrame = false;
  private beginT = new Float64Array(NA);
  private openB = new Uint8Array(NA);
  private accum = new Float64Array(NA);
  // Completed-frame ring — preallocated, zero steady-state allocation.
  private rStart = new Float64Array(RING);
  private rTotal = new Float64Array(RING);
  private rCpu = new Float64Array(RING);
  private rBucket = new Float64Array(RING * NA);
  private head = 0;
  private count = 0;

  constructor(clock?: () => number) {
    this.clock = clock ?? (() => performance.now());
  }

  frameStart(nowMs: number): void {
    if (!this.enabled) {
      this.hasFrame = false; // re-enable starts from a clean frame
      return;
    }
    if (this.hasFrame) {
      const h = this.head;
      this.rStart[h] = this.startT;
      this.rTotal[h] = nowMs - this.startT;
      this.rCpu[h] = this.cpuSpan;
      for (let i = 0; i < NA; i++) this.rBucket[h * NA + i] = this.accum[i];
      this.head = (h + 1) % RING;
      if (this.count < RING) this.count++;
    }
    this.startT = nowMs;
    this.cpuStartT = this.clock();
    this.cpuSpan = 0;
    this.hasFrame = true;
    this.accum.fill(0);
    this.openB.fill(0);
  }

  begin(b: PerfZone): void {
    if (!this.enabled) return;
    const i = IDX[b];
    this.openB[i] = 1;
    this.beginT[i] = this.clock();
  }

  /** An end() whose begin() never ran this frame is IGNORED — an early
   *  return/continue in frame() must never crash the game via the profiler. */
  end(b: PerfZone): void {
    if (!this.enabled) return;
    const i = IDX[b];
    if (!this.openB[i]) return;
    this.openB[i] = 0;
    this.accum[i] += this.clock() - this.beginT[i];
  }

  /** Stamp the end of measured CPU work — call as the LAST statement of frame(),
   *  so late work (e.g. the dialog portrait render) lands in `other`, not gpu. */
  frameEnd(): void {
    if (this.enabled && this.hasFrame) this.cpuSpan = this.clock() - this.cpuStartT;
  }

  stats(nowMs: number): PerfStats {
    const avg = new Array<number>(NA).fill(0);
    const worst = new Array<number>(NA).fill(0);
    let frames = 0;
    let frameAvg = 0, frameWorst = 0, otherAvg = 0, otherWorst = 0, gpuAvg = 0, gpuWorst = 0;
    let hot = -1, hotTotal = 0;
    for (let k = 0; k < this.count; k++) {
      const h = (this.head - 1 - k + RING) % RING;
      // Age by frame END, not start: a hitch frame LONGER than the avg window
      // must still count in the paint right after it — that's the reading that
      // answers "what was that stutter". Ring scans newest-first from head-1.
      const endAge = nowMs - (this.rStart[h] + this.rTotal[h]);
      if (endAge > WORST_WINDOW_MS) break;
      const total = this.rTotal[h];
      const cpu = this.rCpu[h];
      let sum = 0;
      for (let i = 0; i < NA; i++) {
        const v = this.rBucket[h * NA + i];
        if (i < N) sum += v; // subs re-measure time inside their parent — top-level only
        if (v > worst[i]) worst[i] = v;
      }
      const other = Math.max(0, cpu - sum);
      const gpu = Math.max(0, total - cpu);
      if (other > otherWorst) otherWorst = other;
      if (gpu > gpuWorst) gpuWorst = gpu;
      if (total > frameWorst) frameWorst = total;
      if (total > hotTotal) {
        hotTotal = total;
        hot = 0;
        for (let i = 1; i < N; i++) if (this.rBucket[h * NA + i] > this.rBucket[h * NA + hot]) hot = i;
      }
      if (endAge <= AVG_WINDOW_MS) {
        frames++;
        frameAvg += total;
        otherAvg += other;
        gpuAvg += gpu;
        for (let i = 0; i < NA; i++) avg[i] += this.rBucket[h * NA + i];
      }
    }
    if (frames > 0) {
      frameAvg /= frames;
      otherAvg /= frames;
      gpuAvg /= frames;
      for (let i = 0; i < NA; i++) avg[i] /= frames;
    }
    return {
      fps: frameAvg > 0 ? 1000 / frameAvg : 0,
      frameAvg, frameWorst, avg, worst, otherAvg, otherWorst, gpuAvg, gpuWorst, hot,
    };
  }
}


/**
 * GPU pass zones — the frame's render submissions in ORDER (the order is
 * load-bearing, see GpuTimer):
 *   wx-bake     cloud-deck bakes (system.bakeWeather)
 *   reflection  water mirror pass (river or ocean)
 *   shadow      the sun/moon shadow maps — three renders them at the top of the
 *               outermost partition slice's render(); the split is marked by a
 *               sentinel object whose onBeforeRender fires as the first draw
 *   far/mid/near  the depth-partition slices (world/depthPartition.ts), drawn
 *               far to near
 *   volume      volumetric atmosphere march + reprojection
 *   post        soft chain + ink chain + composite
 * Each zone also records the draw calls / triangles it submitted, read as
 * deltas of renderer.info (autoReset is switched off while the timer runs).
 */
export const GPU_ZONES = ["wx-bake", "reflection", "shadow", "far", "mid", "near", "volume", "post"] as const;
export type GpuZone = (typeof GPU_ZONES)[number];
const NG = GPU_ZONES.length;
const GIDX = Object.fromEntries(GPU_ZONES.map((z, i) => [z, i])) as Record<GpuZone, number>;

export interface GpuStats {
  /** true when the extension exists and the timer is attached. */
  supported: boolean;
  /** Complete sampling cycles resolved into the window (each mark has this
   *  many cumulative samples). */
  frames: number;
  /** Per-zone ms, GPU_ZONES order: the mean cumulative mark at this zone's end
   *  minus the mean at the previous zone's end. */
  avg: number[];
  /** Per-zone worst single sample of the same difference (against the mean of
   *  the previous mark), GPU_ZONES order. */
  worst: number[];
  /** Per-zone draw calls / triangles per frame (exact, averaged over recent frames). */
  calls: number[];
  tris: number[];
  /** Independent whole-render query mean; never the sum of estimated zones. */
  totalAvg: number;
}

/** Structural slice of THREE.WebGLRenderer.info — perf.ts stays three-free. */
export interface PerfInfoSource {
  render: { calls: number; triangles: number };
  autoReset: boolean;
  reset(): void;
}

interface GpuQueryExt { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number }

/** Cumulative samples kept per zone (× NG frames of history). */
const GPU_SAMPLES = 8;
// An odd nine-frame rotation visits both phases of half-rate reflections (and
// all phases of four/eight-frame work). The ninth slot measures the whole render
// independently; never derive the total by clamping noisy cumulative estimates.
const GPU_MARKS = NG + 1;
/** Frames of exact draw/triangle counts averaged. */
const GPU_COUNT_FRAMES = 32;

/**
 * GPU pass timer built on EXT_disjoint_timer_query_webgl2 — ONE query per
 * frame: eight cumulative endpoints and one independent whole-render sample.
 *
 * WHY NOT A QUERY PER ZONE. Measured 2026-09-21 on ANGLE/Metal (M4 Pro):
 * bracketing each of the eight passes with its own TIME_ELAPSED query made the
 * zone times sum to 198 ms inside a 72 ms frame and doubled the CPU render
 * span. The backend ends a command buffer at every query boundary, and each
 * elapsed span then absorbs the scheduling gaps and neighbours' work. A single
 * bracket per frame (what client/test/landscapes/profile-sky.mjs uses) is
 * cheap and honest.
 *
 * SO: every frame opens one query at the first zone's begin and closes it at
 * the END of a target zone. A ninth slot closes at frameEnd instead, measuring
 * the whole render. Nine is coprime to the two/four/eight-frame update cadences,
 * so eight samples of each endpoint span their phases instead of aliasing them.
 * Estimates still require stationary work over the window: this is sampling,
 * not eight simultaneous per-pass timers or an exact per-pass hitch trace.
 * The sample is the cumulative GPU time from the frame's first submission to
 * that boundary. A zone's cost is the difference between the mean cumulative
 * mark at its end and the mean at the previous zone's end — sound as long as
 * the zones begin in GPU_ZONES order within a frame, which the frame loop
 * guarantees, and a zone that does not run in a frame simply contributes a
 * zero-length step (the query closes at the next zone's begin). Draw calls and
 * triangles per zone are exact every frame.
 *
 * Elapsed time is GPU WALL time between the marks: if the CPU starves the GPU
 * mid-frame, the pass that spans the bubble absorbs it. Read alongside the CPU
 * rows.
 */
export class GpuTimer {
  private gl: WebGL2RenderingContext | null = null;
  private ext: GpuQueryExt | null = null;
  private info: PerfInfoSource | null = null;
  private autoReset0 = true;
  private running = false;
  private disjoint = false;
  private pool: WebGLQuery[] = [];
  /** This frame's query (null once closed or if none opened yet). */
  private query: WebGLQuery | null = null;
  private queryOpen = false;
  /** Zone whose END closes this frame's query. */
  private target = 0;
  private curZone = -1;
  private calls0 = 0;
  private tris0 = 0;
  private curCalls = new Float64Array(NG);
  private curTris = new Float64Array(NG);
  /** Frames awaiting query results, oldest first. */
  private pending: { q: WebGLQuery; target: number; calls: Float64Array; tris: Float64Array }[] = [];
  /** Cumulative marks per zone: ring of GPU_SAMPLES. */
  private cum = new Float64Array(GPU_MARKS * GPU_SAMPLES);
  private cumHead = new Uint8Array(GPU_MARKS);
  private cumCount = new Uint8Array(GPU_MARKS);
  /** Exact counts per frame: ring of GPU_COUNT_FRAMES. */
  private rCalls = new Float64Array(NG * GPU_COUNT_FRAMES);
  private rTris = new Float64Array(NG * GPU_COUNT_FRAMES);
  private cntHead = 0;
  private cntCount = 0;

  get supported(): boolean { return !!this.ext; }

  /** Attach to the live context. Safe to call once; a missing extension leaves
   *  the timer inert (every method a no-op) and `supported` false. */
  attach(gl: WebGLRenderingContext | WebGL2RenderingContext, info: PerfInfoSource): void {
    if (!("createQuery" in gl)) return;
    const ext = gl.getExtension("EXT_disjoint_timer_query_webgl2") as GpuQueryExt | null;
    if (!ext) return;
    this.gl = gl as WebGL2RenderingContext;
    this.ext = ext;
    this.info = info;
  }

  /** Called on overlay enable/disable — info.autoReset is the one renderer
   *  setting the timer owns while it runs (three resets the counters at the top
   *  of every render() call, and a frame has 6-12 of them). */
  setRunning(on: boolean): void {
    if (!this.info || on === this.running) return;
    this.running = on;
    if (on) { this.autoReset0 = this.info.autoReset; this.info.autoReset = false; }
    else {
      this.closeQuery();
      this.info.autoReset = this.autoReset0;
      for (const f of this.pending) this.pool.push(f.q);
      if (this.query) this.pool.push(this.query);
      this.pending.length = 0;
      this.query = null;
      this.curZone = -1;
      this.cum.fill(0); this.cumHead.fill(0); this.cumCount.fill(0);
      this.cntHead = this.cntCount = this.target = 0;
    }
  }

  /** Top of the frame: file the previous frame, harvest resolved frames,
   *  advance the round-robin target, reset the draw counters. */
  frameStart(): void {
    const gl = this.gl, ext = this.ext, info = this.info;
    if (!this.running || !gl || !ext || !info) return;
    this.endZone();
    this.closeQuery();
    if (this.query) {
      this.pending.push({ q: this.query, target: this.target, calls: this.curCalls.slice(), tris: this.curTris.slice() });
      this.query = null;
      this.target = (this.target + 1) % GPU_MARKS;
    }
    this.curCalls.fill(0);
    this.curTris.fill(0);
    this.curZone = -1;
    // Resolve in order; a frame whose query is not back yet blocks the ones
    // behind it (they will not be back either).
    const disjoint = this.disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
    // A disjoint invalidates every outstanding query, including results that
    // have not become available yet. They cannot be accepted on a later frame.
    if (disjoint) {
      for (const f of this.pending) this.pool.push(f.q);
      this.pending.length = 0;
    }
    while (this.pending.length) {
      const f = this.pending[0];
      if (!gl.getQueryParameter(f.q, gl.QUERY_RESULT_AVAILABLE)) break;
      this.pending.shift();
      if (!(gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean)) {
        const ms = (gl.getQueryParameter(f.q, gl.QUERY_RESULT) as number) / 1e6;
        const z = f.target;
        this.cum[z * GPU_SAMPLES + this.cumHead[z]] = ms;
        this.cumHead[z] = (this.cumHead[z] + 1) % GPU_SAMPLES;
        if (this.cumCount[z] < GPU_SAMPLES) this.cumCount[z]++;
        const base = this.cntHead * NG;
        for (let i = 0; i < NG; i++) { this.rCalls[base + i] = f.calls[i]; this.rTris[base + i] = f.tris[i]; }
        this.cntHead = (this.cntHead + 1) % GPU_COUNT_FRAMES;
        if (this.cntCount < GPU_COUNT_FRAMES) this.cntCount++;
      }
      this.pool.push(f.q);
    }
    // Pending frames pile up only if results stop arriving (context lost, tab
    // hidden): cap them so a stalled tab cannot leak queries.
    while (this.pending.length > 8) this.pool.push(this.pending.shift()!.q);
    info.reset();
  }

  /** Open zone `zone` (closing the previous one). Zones must begin in
   *  GPU_ZONES order within a frame; an out-of-order begin is ignored for the
   *  query (its counts still register). */
  begin(zone: GpuZone): void {
    const gl = this.gl, ext = this.ext, info = this.info;
    if (!this.running || !gl || !ext || !info) return;
    const z = GIDX[zone];
    this.endZone();
    this.curZone = z;
    this.calls0 = info.render.calls;
    this.tris0 = info.render.triangles;
    if (this.disjoint) return;
    if (this.query === null) {
      // First zone of the frame opens the query.
      const q = this.pool.pop() ?? gl.createQuery();
      if (!q) return;
      gl.beginQuery(ext.TIME_ELAPSED_EXT, q);
      this.query = q;
      this.queryOpen = true;
    }
    // The target zone's end is "the begin of the first later zone" — a target
    // that did not run this frame closes here with a zero-length step.
    if (this.queryOpen && z > this.target) this.closeQuery();
  }

  end(): void { this.endZone(); if (this.queryOpen && this.curZone === -1 && this.lastZone >= this.target) this.closeQuery(); }

  /** End of submissions, even when post/volume or other trailing zones skip.
   * Closing at the next rAF would include idle/compositor time in those marks. */
  frameEnd(): void { if (this.running) { this.endZone(); this.closeQuery(); } }

  private lastZone = -1;

  private endZone(): void {
    const info = this.info;
    if (!info || this.curZone < 0) return;
    this.curCalls[this.curZone] += info.render.calls - this.calls0;
    this.curTris[this.curZone] += info.render.triangles - this.tris0;
    this.lastZone = this.curZone;
    this.curZone = -1;
  }

  private closeQuery(): void {
    const gl = this.gl, ext = this.ext;
    if (!gl || !ext || !this.queryOpen) return;
    gl.endQuery(ext.TIME_ELAPSED_EXT);
    this.queryOpen = false;
  }

  stats(): GpuStats {
    const avg = new Array<number>(NG).fill(0), worst = new Array<number>(NG).fill(0);
    const calls = new Array<number>(NG).fill(0), tris = new Array<number>(NG).fill(0);
    const mean = new Array<number>(GPU_MARKS).fill(0);
    let frames = GPU_SAMPLES;
    for (let z = 0; z < GPU_MARKS; z++) {
      const n = this.cumCount[z];
      frames = Math.min(frames, n);
      let sum = 0;
      for (let k = 0; k < n; k++) sum += this.cum[z * GPU_SAMPLES + k];
      mean[z] = n ? sum / n : 0;
    }
    // Sampling noise can make cumulative means non-monotonic. Bound estimates
    // to the independently measured total; never inflate that total to hide a
    // negative step. Suppress pass estimates until every endpoint has a sample.
    const totalAvg = mean[NG];
    let prev = 0;
    for (let z = 0; z < NG; z++) {
      const n = this.cumCount[z];
      const m = frames ? (z === NG - 1 ? totalAvg : Math.min(totalAvg, Math.max(prev, mean[z]))) : 0;
      avg[z] = m - prev;
      let w = 0;
      for (let k = 0; k < n; k++) w = Math.max(w, this.cum[z * GPU_SAMPLES + k] - prev);
      worst[z] = w;
      prev = m;
    }
    const nc = this.cntCount;
    for (let k = 0; k < nc; k++) for (let i = 0; i < NG; i++) { calls[i] += this.rCalls[k * NG + i]; tris[i] += this.rTris[k * NG + i]; }
    if (nc) for (let i = 0; i < NG; i++) { calls[i] /= nc; tris[i] /= nc; }
    return { supported: this.supported, frames, avg, worst, calls, tris, totalAvg };
  }
}

/**
 * Scene census — where the triangles ARE, by kind and by distance from the eye.
 * Walked at paint time (≈4 Hz) by a three-aware walker the host installs
 * (engine/renderCensus.ts); this module only holds the table shape.
 */
export const CENSUS_KINDS = ["terrain", "tree", "cover", "grass", "rock", "scatter", "building", "actor", "water", "sky", "other"] as const;
export type CensusKind = (typeof CENSUS_KINDS)[number];
/** Upper edges of the distance bands, metres from the eye. */
export const CENSUS_BANDS = [50, 150, 400, 1000, 5000, Infinity] as const;
export interface CensusTable {
  /** tris[kind][band] — triangles resident in visible, frustum-intersecting meshes. */
  tris: number[][];
  /** meshes[kind][band] — object count (an InstancedMesh counts once). */
  meshes: number[][];
  /** instances[kind][band] */
  instances: number[][];
  /** Same, for shadow CASTERS only (castShadow && visible), any band. */
  casterTris: number[];
  casterMeshes: number[];
}
export function emptyCensus(): CensusTable {
  const grid = () => CENSUS_KINDS.map(() => CENSUS_BANDS.map(() => 0));
  return { tris: grid(), meshes: grid(), instances: grid(), casterTris: CENSUS_KINDS.map(() => 0), casterMeshes: CENSUS_KINDS.map(() => 0) };
}
export type CensusWalker = (out: CensusTable) => void;

const LS_KEY = "hl-perf";
/** Overlay anchor — the one knob to move it if it collides with a HUD element. */
const ANCHOR = { top: "12px", right: "12px" } as const;
const BUDGET_MS = 16.7;
const PAINT_EVERY_MS = 250; // ~4 Hz — reading the overlay must not cost frames
/** Overlay rows: each top-level bucket, its indented sub-zones right under it,
 *  then the two derived rows (zi -1 = other, -2 = gpu/vsync). */
interface RowSpec { label: string; zi: number; sub: boolean }
const ROW_SPECS: RowSpec[] = PERF_BUCKETS.flatMap((b): RowSpec[] => [
  { label: b, zi: IDX[b], sub: false },
  ...((PERF_SUBS as Partial<Record<PerfBucket, readonly PerfSub[]>>)[b] ?? [])
    .map((s): RowSpec => ({ label: s, zi: IDX[s], sub: true })),
]);
ROW_SPECS.push({ label: "other", zi: -1, sub: false }, { label: "gpu/vsync", zi: -2, sub: false });

export interface PerfRendererInfo {
  render: { calls: number; triangles: number };
  memory: { geometries: number; textures: number };
}

/** Structural slice of THREE.Scene — perf.ts stays three-free. */
interface PerfScene {
  traverse(cb: (o: unknown) => void): void;
}

interface PerfRow {
  spec: RowSpec;
  row: HTMLElement;
  bar: HTMLElement;
  avg: HTMLElement;
  worst: HTMLElement;
}

class PerfMonitor extends PerfCore {
  private godAllowed = false;
  private el: HTMLElement | null = null;
  private rows: PerfRow[] = [];
  private gpuRows: PerfRow[] = [];
  private censusEl: HTMLElement | null = null;
  private footer: HTMLElement | null = null;
  private lastPaint = 0;
  private reflDraws: number | null = null;
  private scene: PerfScene | null = null;
  /** GPU pass timer — inert until attachRenderer() finds the extension. */
  readonly gpu = new GpuTimer();
  private censusWalker: CensusWalker | null = null;
  private censusTable = emptyCensus();
  private censusAt = 0;

  /** Optional: lets the footer count visible skinned meshes/bones (humanoid
   *  rendering pressure). Walked only at paint time (~4 Hz), never per frame. */
  setScene(scene: PerfScene | null): void {
    this.scene = scene;
  }

  /** Hand the timer the live GL context + renderer.info. Call once after the
   *  renderer exists; a context without EXT_disjoint_timer_query_webgl2 (Safari,
   *  some Linux drivers) leaves the GPU rows blank and everything else working. */
  attachRenderer(gl: WebGLRenderingContext | WebGL2RenderingContext, info: PerfInfoSource): void {
    this.gpu.attach(gl, info);
    if (this.enabled) this.gpu.setRunning(true);
  }

  /** Install the three-aware scene walker (engine/renderCensus.ts). Walked at
   *  paint time only (~4 Hz), never per frame. */
  setCensusWalker(walk: CensusWalker | null): void {
    this.censusWalker = walk;
  }

  /** The latest census table (refreshed at paint time while enabled; probes
   *  may call `refreshCensus()` for an immediate walk). */
  census(): CensusTable { return this.censusTable; }
  refreshCensus(): CensusTable {
    if (this.censusWalker) {
      this.censusTable = emptyCensus();
      this.censusWalker(this.censusTable);
      this.censusAt = this.clock();
    }
    return this.censusTable;
  }
  gpuStats(): GpuStats { return this.gpu.stats(); }

  /** GPU zone brackets — sequential, see GPU_ZONES. No-ops while disabled. */
  gpuBegin(zone: GpuZone): void { if (this.enabled) this.gpu.begin(zone); }
  gpuEnd(): void { if (this.enabled) this.gpu.end(); }

  override frameStart(nowMs: number): void {
    super.frameStart(nowMs);
    if (this.enabled) this.gpu.frameStart();
  }

  /** Stamp the reflection pass's draw-call count (read renderer.info.render.calls
   *  right after oceanPatch.renderReflection — each render() resets the counters,
   *  so that read is the reflection pass alone). Pass null when the pass no-oped. */
  noteReflectionDraws(calls: number | null): void {
    if (this.enabled) this.reflDraws = calls;
  }

  constructor() {
    super();
    this.enabled = false; // off until god-verified AND toggled/persisted on
  }

  /** Server-verified god flag (net.onGod). Enabling is impossible without it;
   *  losing it tears the overlay down — so a stale localStorage flag on a
   *  non-god account can never resurrect the overlay. */
  setGodAllowed(on: boolean): void {
    this.godAllowed = on;
    if (on) {
      if (localStorage.getItem(LS_KEY) === "1") this.setEnabled(true);
    } else if (this.enabled) {
      // Teardown WITHOUT touching the persisted preference: only a user toggle
      // writes it, so a transient revoke (reconnect race) still auto-restores
      // the overlay when the next welcome re-grants god.
      this.enabled = false;
      this.gpu.setRunning(false);
      this.unmount();
    }
  }

  get isOn(): boolean {
    return this.enabled;
  }

  setEnabled(on: boolean): void {
    if (on && !this.godAllowed) return;
    if (on === this.enabled) return;
    this.enabled = on;
    try {
      localStorage.setItem(LS_KEY, on ? "1" : "0");
    } catch { /* storage may be unavailable (private mode) — overlay still works */ }
    this.gpu.setRunning(on);
    if (on) this.mount();
    else this.unmount();
  }

  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /** Call after renderer.render as the last profiler touch of the frame:
   *  closes the measured CPU span and repaints the overlay at ~4 Hz. */
  frameDone(info: PerfRendererInfo): void {
    if (this.enabled) this.gpu.frameEnd();
    this.frameEnd();
    if (!this.enabled || !this.el) return;
    const now = this.clock();
    if (now - this.lastPaint < PAINT_EVERY_MS) return;
    this.lastPaint = now;
    this.paint(this.stats(now), info);
  }

  private mount(): void {
    if (this.el) return;
    const el = document.createElement("div");
    el.id = "perf-overlay";
    el.style.top = ANCHOR.top;
    el.style.right = ANCHOR.right;
    const head = document.createElement("div");
    head.className = "perf-row perf-head";
    head.innerHTML =
      `<span>BUCKET</span><span></span><span class="perf-num">AVG</span><span class="perf-num">MAX</span>`;
    el.appendChild(head);
    this.rows = [];
    for (const spec of ROW_SPECS) {
      const row = document.createElement("div");
      row.className = spec.sub ? "perf-row perf-sub" : "perf-row";
      row.innerHTML =
        `<span>${spec.sub ? "· " : ""}${spec.label.toUpperCase()}</span>` +
        `<span class="perf-track"><span class="perf-bar"></span></span>` +
        `<span class="perf-num perf-avg"></span><span class="perf-num perf-worst"></span>`;
      el.appendChild(row);
      this.rows.push({
        spec,
        row,
        bar: row.querySelector<HTMLElement>(".perf-bar")!,
        avg: row.querySelector<HTMLElement>(".perf-avg")!,
        worst: row.querySelector<HTMLElement>(".perf-worst")!,
      });
    }
    // GPU pass rows (timer-query ms, then the pass's draw calls / triangles).
    const ghead = document.createElement("div");
    ghead.className = "perf-row perf-head perf-gpu-head";
    ghead.innerHTML =
      `<span>GPU PASS</span><span class="perf-gpu-cnt">DRAWS · TRIS</span><span class="perf-num">AVG</span><span class="perf-num">MAX</span>`;
    el.appendChild(ghead);
    this.gpuRows = [];
    for (let i = 0; i < GPU_ZONES.length; i++) {
      const row = document.createElement("div");
      row.className = "perf-row perf-gpu";
      row.innerHTML =
        `<span>${GPU_ZONES[i].toUpperCase()}</span>` +
        `<span class="perf-track"><span class="perf-bar"></span><span class="perf-gpu-cnt"></span></span>` +
        `<span class="perf-num perf-avg"></span><span class="perf-num perf-worst"></span>`;
      el.appendChild(row);
      this.gpuRows.push({
        spec: { label: GPU_ZONES[i], zi: i, sub: false },
        row,
        bar: row.querySelector<HTMLElement>(".perf-bar")!,
        avg: row.querySelector<HTMLElement>(".perf-avg")!,
        worst: row.querySelector<HTMLElement>(".perf-worst")!,
      });
    }
    const census = document.createElement("pre");
    census.className = "perf-census";
    el.appendChild(census);
    this.censusEl = census;
    const foot = document.createElement("div");
    foot.className = "perf-foot";
    el.appendChild(foot);
    this.footer = foot;
    document.body.appendChild(el);
    this.el = el;
    this.lastPaint = 0;
  }

  private unmount(): void {
    this.el?.remove();
    this.el = null;
    this.rows = [];
    this.gpuRows = [];
    this.censusEl = null;
    this.footer = null;
  }

  /** Retained nodes, textContent/width writes only — no innerHTML churn, no
   *  layout thrash (matches hud.ts discipline). */
  private paint(s: PerfStats, info: PerfRendererInfo): void {
    for (const r of this.rows) {
      const zi = r.spec.zi;
      const avg = zi >= 0 ? s.avg[zi] : zi === -1 ? s.otherAvg : s.gpuAvg;
      const worst = zi >= 0 ? s.worst[zi] : zi === -1 ? s.otherWorst : s.gpuWorst;
      r.bar.style.width = `${Math.min(100, (avg / BUDGET_MS) * 100)}%`;
      r.avg.textContent = avg.toFixed(1);
      r.worst.textContent = worst.toFixed(1);
      r.row.classList.toggle("perf-hot", !r.spec.sub && zi === s.hot);
    }
    const g = this.gpu.stats();
    for (const r of this.gpuRows) {
      const zi = r.spec.zi;
      const avg = g.avg[zi], worst = g.worst[zi];
      r.bar.style.width = `${Math.min(100, (avg / BUDGET_MS) * 100)}%`;
      r.avg.textContent = g.supported ? avg.toFixed(1) : "n/a";
      r.worst.textContent = g.supported ? worst.toFixed(1) : "";
      const cnt = r.row.querySelector<HTMLElement>(".perf-gpu-cnt")!;
      cnt.textContent = g.frames ? `${Math.round(g.calls[zi])} · ${fmtK(g.tris[zi])}` : "";
    }
    // The census is the draw-distance answer: triangles by kind × eye distance.
    if (this.censusEl && this.censusWalker) {
      const now = this.clock();
      if (now - this.censusAt > PAINT_EVERY_MS * 2) this.refreshCensus();
      this.censusEl.textContent = formatCensus(this.censusTable);
    }
    // reflDraws is the reflection pass's own submission count, stamped from
    // main.ts; `info` counters are per FRAME while the GPU timer holds
    // autoReset off (every pass summed), per last render() call otherwise.
    const refl = this.reflDraws !== null ? ` (refl ${this.reflDraws})` : "";
    // Humanoid rendering pressure: every visible SkinnedMesh re-uploads its
    // bone texture inside renderer.render — a per-mesh CPU cost DRAW can't
    // itemize, so surface the population here.
    let skins = 0, bones = 0;
    this.scene?.traverse((o) => {
      const m = o as { isSkinnedMesh?: boolean; visible?: boolean; skeleton?: { bones: unknown[] } };
      if (m.isSkinnedMesh && m.visible !== false) {
        skins++;
        bones += m.skeleton?.bones.length ?? 0;
      }
    });
    this.footer!.textContent =
      `${s.fps.toFixed(0)} FPS · frame ${s.frameAvg.toFixed(1)}/${s.frameWorst.toFixed(1)}ms · ` +
      `${info.render.calls} draws${refl} · ${(info.render.triangles / 1000).toFixed(0)}k tris · ` +
      `${info.memory.geometries} geo · ${info.memory.textures} tex · ` +
      `${skins} skin/${(bones / 1000).toFixed(1)}k bones`;
  }
}

function fmtK(n: number): string {
  return n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : `${Math.round(n)}`;
}

/** Fixed-width text grid: kinds down, distance bands across, k-triangles in
 *  frustum-intersecting visible meshes; last column = shadow-caster tris. */
export function formatCensus(t: CensusTable): string {
  const bands = CENSUS_BANDS.map((b, i) => (b === Infinity ? `>${CENSUS_BANDS[i - 1] / 1000}k` : b >= 1000 ? `<${b / 1000}k` : `<${b}`));
  const w = 7;
  const head = "TRIS".padEnd(9) + bands.map((b) => b.padStart(w)).join("") + "  CAST".padStart(w + 1);
  const lines = [head];
  const total = CENSUS_BANDS.map(() => 0);
  let castTotal = 0;
  CENSUS_KINDS.forEach((k, i) => {
    const row = t.tris[i];
    const sum = row.reduce((a, b) => a + b, 0);
    row.forEach((v, j) => (total[j] += v));
    castTotal += t.casterTris[i];
    if (sum === 0 && t.casterTris[i] === 0) return;
    lines.push(k.padEnd(9) + row.map((v) => fmtK(v).padStart(w)).join("") + fmtK(t.casterTris[i]).padStart(w + 2));
  });
  lines.push("all".padEnd(9) + total.map((v) => fmtK(v).padStart(w)).join("") + fmtK(castTotal).padStart(w + 2));
  return lines.join("\n");
}

export const perf = new PerfMonitor();

// HMR: the old module's overlay element must not linger next to the new one's.
import.meta.hot?.dispose(() => perf.setEnabled(false));
